'use client';

import { useEffect, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  cleanMarkdown,
  countMarkdownContent,
  createJsonDownloadText,
  createMarkdownDiff,
  extractMarkdownLinks,
  generateMarkdownChecklist,
  renderMarkdownDiffHtml,
  renderMarkdownPreview,
  renderMarkdownToHtml,
  convertHtmlToMarkdown,
  type MarkdownCounts,
  type MarkdownDiffLine,
  type MarkdownLinkEntry,
} from '@/lib/browser-markdown-tools';

type Variant =
  | 'markdown-editor'
  | 'markdown-preview'
  | 'markdown-to-html'
  | 'html-to-markdown'
  | 'markdown-cleaner'
  | 'markdown-link-extractor'
  | 'markdown-diff-viewer'
  | 'markdown-checklist-generator'
  | 'markdown-word-counter';

type ToolResult =
  | {
      kind: 'markdown';
      markdown: string;
      html: string;
      counts: MarkdownCounts;
    }
  | {
      kind: 'html';
      html: string;
      previewHtml: string;
    }
  | {
      kind: 'links';
      links: MarkdownLinkEntry[];
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'diff';
      diffHtml: string;
      summaryText: string;
      lines: MarkdownDiffLine[];
    }
  | {
      kind: 'counts';
      counts: MarkdownCounts;
      plainText: string;
      jsonText: string;
    };

const sampleMarkdown = `# Markdown ready

Write with **GFM** support.

- Task lists
- Tables
- Code fences

## Example table

| Tool | Output |
| :--- | ---: |
| Preview | Live |
| Export | Local |`;

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel?: string;
    successLabel?: string;
    primaryPlaceholder: string;
    secondaryPlaceholder?: string;
    previewMode?: 'markdown' | 'html';
  }
> = {
  'markdown-editor': {
    title: 'Write Markdown with a live GFM preview',
    description:
      'Type or paste Markdown, check the rendered output instantly, and copy or download the `.md` source locally.',
    badge: 'Editor',
    primaryPlaceholder: sampleMarkdown,
    previewMode: 'markdown',
  },
  'markdown-preview': {
    title: 'Preview Markdown online with GitHub-flavored rendering',
    description:
      'Paste Markdown, inspect the rendered result, and keep the source copy-ready without opening a docs app.',
    badge: 'Preview',
    primaryPlaceholder: sampleMarkdown,
    previewMode: 'markdown',
  },
  'markdown-to-html': {
    title: 'Convert Markdown into sanitized HTML',
    description:
      'Paste Markdown, render browser-safe HTML, and copy or download the finished `.html` output locally.',
    badge: 'Convert',
    actionLabel: 'Convert to HTML',
    successLabel: 'Markdown to HTML conversion',
    primaryPlaceholder: sampleMarkdown,
    previewMode: 'html',
  },
  'html-to-markdown': {
    title: 'Convert practical HTML into Markdown',
    description:
      'Paste HTML snippets, convert them locally into Markdown, and review the resulting GFM preview before export.',
    badge: 'Convert',
    actionLabel: 'Convert to Markdown',
    successLabel: 'HTML to Markdown conversion',
    primaryPlaceholder:
      '<h1>Shipping checklist</h1><p>Keep the structure simple.</p><ul><li>Review links</li><li>Export clean Markdown</li></ul>',
    previewMode: 'markdown',
  },
  'markdown-cleaner': {
    title: 'Clean Markdown spacing without rewriting the content',
    description:
      'Normalize blank lines, heading spacing, list spacing, fence spacing, and trailing whitespace while keeping the same meaning.',
    badge: 'Clean',
    actionLabel: 'Clean Markdown',
    successLabel: 'Markdown cleanup',
    primaryPlaceholder: '#Heading\n\n\n-Item one\n-Item two  \n\n```ts\nconsole.log(\"hi\")\n```',
    previewMode: 'markdown',
  },
  'markdown-link-extractor': {
    title: 'Extract links from Markdown content',
    description:
      'Inspect inline links, image links, reference links, and autolinks from one Markdown block, then copy text or JSON output.',
    badge: 'Links',
    actionLabel: 'Extract links',
    successLabel: 'Markdown link extraction',
    primaryPlaceholder:
      '[Docs](https://cleanor.app)\n\n![Preview](https://cleanor.app/image.png)\n\n<https://example.com/help>',
  },
  'markdown-diff-viewer': {
    title: 'Compare two Markdown sources and inspect the text diff',
    description:
      'Paste the original and updated Markdown, compare the source changes locally, and copy the diff summary without rendering noise.',
    badge: 'Diff',
    actionLabel: 'Compare Markdown',
    successLabel: 'Markdown diff',
    primaryPlaceholder: '# Changelog\n\n- Keep old title\n- Preserve one bullet',
    secondaryPlaceholder:
      '# Release notes\n\n- Keep updated title\n- Preserve one bullet\n- Add one more item',
  },
  'markdown-checklist-generator': {
    title: 'Turn plain items into a Markdown task list',
    description:
      'Paste lines or comma-separated items, choose the default checked state, and generate one clean checklist block locally.',
    badge: 'Checklist',
    actionLabel: 'Generate checklist',
    successLabel: 'Markdown checklist generation',
    primaryPlaceholder: 'Plan launch\nWrite docs\nReview links',
    previewMode: 'markdown',
  },
  'markdown-word-counter': {
    title: 'Count words, headings, links, and list items in Markdown',
    description:
      'Inspect one Markdown block and get stable counts for words, characters, headings, links, and list items locally.',
    badge: 'Count',
    actionLabel: 'Analyze Markdown',
    successLabel: 'Markdown analysis',
    primaryPlaceholder: sampleMarkdown,
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

function buildLinkPlainText(links: MarkdownLinkEntry[]) {
  return links.map((entry) => `[${entry.type}] ${entry.text} -> ${entry.url}`).join('\n');
}

function buildCountsPlainText(counts: MarkdownCounts) {
  return [
    `Words: ${counts.words}`,
    `Characters: ${counts.characters}`,
    `Characters without spaces: ${counts.charactersNoSpaces}`,
    `Headings: ${counts.headings}`,
    `Links: ${counts.links}`,
    `List items: ${counts.listItems}`,
  ].join('\n');
}

export default function BrowserMarkdownTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState(
    variant === 'markdown-editor' || variant === 'markdown-preview' ? sampleMarkdown : '',
  );
  const [secondaryValue, setSecondaryValue] = useState('');
  const [checklistChecked, setChecklistChecked] = useState(false);
  const [liveMarkdownHtml, setLiveMarkdownHtml] = useState('');
  const [liveCounts, setLiveCounts] = useState<MarkdownCounts | null>(null);
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (variant !== 'markdown-editor' && variant !== 'markdown-preview') {
      return;
    }

    let cancelled = false;

    void renderMarkdownPreview(inputValue).then(
      (preview) => {
        if (cancelled) {
          return;
        }
        setLiveMarkdownHtml(preview.html);
        setLiveCounts(preview.counts);
      },
      () => {
        if (cancelled) {
          return;
        }
        setLiveMarkdownHtml('');
        setLiveCounts(null);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [inputValue, variant]);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function runTool() {
    const trimmedInput = inputValue.trim();
    const trimmedSecondary = secondaryValue.trim();

    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      switch (variant) {
        case 'markdown-editor':
        case 'markdown-preview':
          return;
        case 'markdown-to-html':
        case 'html-to-markdown':
        case 'markdown-cleaner':
        case 'markdown-link-extractor':
        case 'markdown-checklist-generator':
        case 'markdown-word-counter':
          if (!trimmedInput) {
            throw new Error('Paste some content first.');
          }
          break;
        case 'markdown-diff-viewer':
          if (!trimmedInput || !trimmedSecondary) {
            throw new Error('Paste both Markdown versions first.');
          }
          break;
      }

      setIsRunning(true);
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another Markdown tool run right now.',
      );

      switch (variant) {
        case 'markdown-to-html': {
          const html = await renderMarkdownToHtml(inputValue);
          setResult({ kind: 'html', html, previewHtml: html });
          break;
        }
        case 'html-to-markdown': {
          const markdown = convertHtmlToMarkdown(inputValue);
          const html = await renderMarkdownToHtml(markdown);
          setResult({
            kind: 'markdown',
            markdown,
            html,
            counts: countMarkdownContent(markdown),
          });
          break;
        }
        case 'markdown-cleaner': {
          const markdown = cleanMarkdown(inputValue);
          const html = await renderMarkdownToHtml(markdown);
          setResult({
            kind: 'markdown',
            markdown,
            html,
            counts: countMarkdownContent(markdown),
          });
          break;
        }
        case 'markdown-link-extractor': {
          const links = extractMarkdownLinks(inputValue);
          const jsonText = createJsonDownloadText(links);
          setResult({
            kind: 'links',
            links,
            plainText: buildLinkPlainText(links),
            jsonText,
          });
          break;
        }
        case 'markdown-diff-viewer': {
          const diff = createMarkdownDiff(inputValue, secondaryValue);
          setResult({
            kind: 'diff',
            diffHtml: renderMarkdownDiffHtml(diff),
            summaryText: diff.summaryText,
            lines: diff.lines,
          });
          break;
        }
        case 'markdown-checklist-generator': {
          const markdown = generateMarkdownChecklist(inputValue, checklistChecked);
          const html = await renderMarkdownToHtml(markdown);
          setResult({
            kind: 'markdown',
            markdown,
            html,
            counts: countMarkdownContent(markdown),
          });
          break;
        }
        case 'markdown-word-counter': {
          const counts = countMarkdownContent(inputValue);
          setResult({
            kind: 'counts',
            counts,
            plainText: buildCountsPlainText(counts),
            jsonText: createJsonDownloadText(counts),
          });
          break;
        }
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel ?? copy.title, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This Markdown task could not be completed.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current Markdown output.');
    }
  }

  const previewHtml =
    variant === 'markdown-editor' || variant === 'markdown-preview'
      ? liveMarkdownHtml
      : result?.kind === 'markdown'
        ? result.html
        : result?.kind === 'html'
          ? result.previewHtml
          : null;

  const activeCounts =
    variant === 'markdown-editor' || variant === 'markdown-preview'
      ? liveCounts
      : result?.kind === 'markdown'
        ? result.counts
        : result?.kind === 'counts'
          ? result.counts
          : null;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>
                {variant === 'html-to-markdown'
                  ? 'HTML input'
                  : variant === 'markdown-diff-viewer'
                    ? 'Original Markdown'
                    : variant === 'markdown-checklist-generator'
                      ? 'Plain items'
                      : 'Markdown input'}
              </span>
              <textarea
                onChange={(event) => {
                  if (variant === 'markdown-editor' || variant === 'markdown-preview') {
                    setCreditMessage(null);
                    setCopyMessage(null);
                  } else {
                    resetOutput();
                  }
                  setInputValue(event.target.value);
                }}
                placeholder={copy.primaryPlaceholder}
                spellCheck={variant !== 'html-to-markdown'}
                value={inputValue}
              />
            </label>

            {variant === 'markdown-diff-viewer' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Updated Markdown</span>
                <textarea
                  onChange={(event) => {
                    resetOutput();
                    setSecondaryValue(event.target.value);
                  }}
                  placeholder={copy.secondaryPlaceholder}
                  value={secondaryValue}
                />
              </label>
            ) : null}

            {variant === 'markdown-checklist-generator' ? (
              <label className="tool-live-checkbox tool-live-field-full">
                <input
                  checked={checklistChecked}
                  onChange={(event) => {
                    resetOutput();
                    setChecklistChecked(event.target.checked);
                  }}
                  type="checkbox"
                />
                <span>Start all checklist items as checked</span>
              </label>
            ) : null}
          </div>

          {copy.actionLabel ? (
            <div className="tool-live-actions">
              <button
                className="button"
                disabled={isRunning}
                onClick={() => void runTool()}
                type="button"
              >
                {isRunning ? 'Working…' : copy.actionLabel}
              </button>
            </div>
          ) : null}

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {variant === 'markdown-editor' || variant === 'markdown-preview' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(inputValue, 'Markdown copied.')}
                type="button"
              >
                Copy Markdown
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  createTextDownload(
                    inputValue,
                    variant === 'markdown-editor' ? 'notes.md' : 'preview.md',
                  )
                }
                type="button"
              >
                Download .md
              </button>
            </div>
          ) : null}

          {result?.kind === 'html' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(result.html, 'HTML copied.')}
                type="button"
              >
                Copy HTML
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  createTextDownload(result.html, 'converted.html', 'text/html;charset=utf-8')
                }
                type="button"
              >
                Download .html
              </button>
            </div>
          ) : null}

          {result?.kind === 'markdown' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(result.markdown, 'Markdown copied.')}
                type="button"
              >
                Copy Markdown
              </button>
              <button
                className="button button-secondary"
                onClick={() => createTextDownload(result.markdown, 'output.md')}
                type="button"
              >
                Download .md
              </button>
            </div>
          ) : null}

          {result?.kind === 'links' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(result.plainText, 'Link list copied.')}
                type="button"
              >
                Copy list
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  createTextDownload(
                    result.jsonText,
                    'markdown-links.json',
                    'application/json;charset=utf-8',
                  )
                }
                type="button"
              >
                Download JSON
              </button>
            </div>
          ) : null}

          {result?.kind === 'diff' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(result.summaryText, 'Diff summary copied.')}
                type="button"
              >
                Copy summary
              </button>
              <button
                className="button button-secondary"
                onClick={() => createTextDownload(result.summaryText, 'markdown-diff-summary.txt')}
                type="button"
              >
                Download summary
              </button>
            </div>
          ) : null}

          {result?.kind === 'counts' ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy(result.plainText, 'Markdown counts copied.')}
                type="button"
              >
                Copy counts
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  createTextDownload(
                    result.jsonText,
                    'markdown-counts.json',
                    'application/json;charset=utf-8',
                  )
                }
                type="button"
              >
                Download JSON
              </button>
            </div>
          ) : null}

          {previewHtml ? (
            <div className="tool-live-preview">
              <h3>
                {variant === 'markdown-to-html'
                  ? 'Rendered preview'
                  : variant === 'html-to-markdown'
                    ? 'Converted Markdown preview'
                    : 'Markdown preview'}
              </h3>
              <div
                className="tool-markdown-rendered"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : null}

          {result?.kind === 'html' ? (
            <div className="tool-live-preview">
              <h3>HTML output</h3>
              <pre className="tool-markdown-code">{result.html}</pre>
            </div>
          ) : null}

          {result?.kind === 'links' ? (
            <div className="tool-live-preview">
              <h3>Extracted links</h3>
              {result.links.length ? (
                <div className="tool-markdown-link-list">
                  {result.links.map((entry, index) => (
                    <article
                      className="tool-markdown-link-card"
                      key={`${entry.type}-${entry.url}-${index}`}
                    >
                      <strong>{entry.text}</strong>
                      <span>{entry.type}</span>
                      <code>{entry.url}</code>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="tool-live-panel-note">
                  <strong>No Markdown links found</strong>
                  <span>Paste Markdown with links, images, or autolinks first.</span>
                </p>
              )}
            </div>
          ) : null}

          {result?.kind === 'diff' ? (
            <>
              <div className="tool-live-panel-note">
                <strong>Diff summary</strong>
                <span>{result.summaryText.replace(/\n/g, ' · ')}</span>
              </div>
              <div className="tool-live-preview">
                <h3>Source diff</h3>
                <div
                  className="tool-markdown-diff"
                  dangerouslySetInnerHTML={{ __html: result.diffHtml }}
                />
              </div>
            </>
          ) : null}

          {(result?.kind === 'counts' ||
            ((variant === 'markdown-editor' || variant === 'markdown-preview') && activeCounts)) &&
          activeCounts ? (
            <div className="tool-live-stats">
              <div>
                <strong>Words</strong>
                <span>{activeCounts.words}</span>
              </div>
              <div>
                <strong>Characters</strong>
                <span>{activeCounts.characters}</span>
              </div>
              <div>
                <strong>No-space characters</strong>
                <span>{activeCounts.charactersNoSpaces}</span>
              </div>
              <div>
                <strong>Links</strong>
                <span>{activeCounts.links}</span>
              </div>
              <div>
                <strong>Headings</strong>
                <span>{activeCounts.headings}</span>
              </div>
              <div>
                <strong>List items</strong>
                <span>{activeCounts.listItems}</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
